import { captureException } from '@sentry/nextjs';

/**
 * GTM 이벤트 타입 정의
 */
export enum GTM_EVENT_TYPE {
  USER_PROPERTY = 'user_property',
  SIGN_UP_STEP = 'sign_up_step',
  SIGN_UP = 'sign_up',
  LOGIN = 'login',
  CLICK_GNB = 'click_gnb',
  CLICK_FNB = 'click_fnb',
  CLICK_SNB = 'click_snb',
  CLICK_SETUP_MENU = 'click_setup_menu',
  CLICK_TAB = 'click_tab',
  CLICK_BTN = 'click_btn',
  START_POST = 'start_post',
  COMPLETE_POST = 'complete_post',
  /** 프로모션 선택 */
  SELECT_PROMOTION = 'select_promotion',
  /** 프로모션 조회 */
  VIEW_PROMOTION = 'view_promotion',
  SELECT_CONTENT = 'select_content',
  VIEW_CONTENT = 'view_content',
  CLICK_BOOKMARK = 'click_bookmark',
  COMPLETE_RECOMMEND = 'complete_recommend',
  COMPLETE_PREFERENCE = 'complete_preference',
  SELECT_ITEM = 'select_item',
  VIEW_ITEM = 'view_item',
  POST_COMMENT = 'post_comment',
  START_DONATION = 'start_donation',
  START_GIFT = 'start_gift',
  PURCHASE = 'purchase',
  BEGIN_CHECKOUT = 'begin_checkout',
  COMPLETE_REVIEW = 'complete_review',
  SEARCH = 'search',
  /** 콘텐츠 공유 */
  SHARE = 'share',
  /** 장바구니 담기 (택소노미 v3.1) */
  ADD_TO_CART = 'add_to_cart',
  /** 장바구니 조회 (택소노미 v3.1) */
  VIEW_CART = 'view_cart',
  /** 장바구니 항목 삭제 (택소노미 v3.1) */
  REMOVE_FROM_CART = 'remove_from_cart',
  /** 웰마켙 즉시 구매 진입 — donation start_gift와 페어 (택소노미 v3.1) */
  START_PURCHASE = 'start_purchase',

  /** @deprecated */
  INVITE_STEP = 'invite_step',
  USE_POINT = 'use_point',
  SELECT_POINT = 'select_point',
  /** @deprecated */
  COMPLETE_INVITE = 'complete_invite',
  CLICK_POINT_BTN = 'click_point_btn',
}

/**
 * 프로모션 영역 타입 정의
 */
export enum PROMOTION_CREATIVE_SLOT {
  WELLO_HOME_MAIN_BANNER = '웰로 홈 메인 배너',
  WELLO_HOME_NEW_BANNER = '웰로 홈 신규 배너',
  WELLO_HOME_BOTTOM_BANNER = '웰로 홈 하단 배너',
  WELLO_DETAIL_PAGE_TOP_BANNER = '웰로 상세 페이지 상단 배너',
  DONATION_HOME_HERO_BANNER = '고향사랑 홈 히어로 배너',
  DONATION_HOME_MONTHLY_PROMOTION_BANNER = '고향사랑 홈 이달의 기획전 배너',
  DONATION_HOME_BELT_BANNER = '고향사랑 홈 띠 배너',
  EVENT_AND_COOPERATION_EVENT = '이벤트 & 제휴 이벤트',
}

/**
 * 콘텐츠 카테고리 타입 정의
 */
export enum CONTENT_CATEGORY {
  POLICY = '정책',
  COMMUNITY = '커뮤니티',
  POLICY_NEWS = '정책 소식',
  POLICY_CARD = '정책카드',
  HOMETOWN_NEWS = '동네 소식',
  EMERGENCY_ROOM = '응급의료기관',
  FESTIVAL = '동네축제',
}

/**
 * GA4 표준 ecommerce items[] 공통 항목 (택소노미 v3.1)
 *
 * 빈 값은 omit — null/undefined를 명시적으로 채워 보내지 않는다.
 */
export interface GtmCommerceItem {
  /** 상품 타입 */
  item_type: '답례품' | '고향사업' | '웰마켙상품';
  /** 상품가격 (할인가, 1개 단위) */
  price: number;
  /** 상품 이름 */
  item_name: string;
  /** 상품 수량 */
  quantity: number;
  /** 상품 고유 번호 — donation: G2025xxx, welmarket: UUID */
  item_id: string;
  /** welmarket 상품 UUID */
  product_uuid?: string;
  /** 할인 전 가격 (welmarket) */
  original_price?: number;
  /** 할인율 % (welmarket) */
  discount_rate?: number;
  /** 입점 브랜드명 (welmarket) */
  product_brand_name?: string;
  /** donation 연동 여부 (welmarket 상품) */
  is_donation_linked?: 'Y' | 'N';
  /** 연결 donation 답례품 ID */
  linked_gift_id?: string;
}

/**
 * 각 이벤트 타입별 파라미터 정의
 */
export type GtmEventParams = {
  [GTM_EVENT_TYPE.USER_PROPERTY]: {
    /** 유저가 로그인했을시, 로그인한 ID */
    user_id?: number;
    /** 로그인 여부 */
    is_login?: string;
    /** 지정기부 기부 횟수 */
    donation_count?: number;
    /** 답례품 구매 횟수 */
    gift_count?: number;
    /** 총 기부 금액 */
    purchase_revenue?: number;
    /** 회원가입을 한 날짜 */
    sign_up_date?: string;
    /** 가장최근 로그인한 날짜 */
    latest_login?: string;
    /** SMS 수신 동의 */
    sms_subscribe?: boolean;
    /** SMS 수신 동의 일시 */
    sms_date?: string;
    /** 위치 수신 동의 */
    location_subscrie?: string;
    /** 위치 수신 동의 일시 */
    location_date?: string;
    /** 가장 최근 기부를 시작한 일시 */
    latest_donation?: string;
    /** 기부주소지 확인 */
    donation_local?: string;
    /** 학력 */
    education_level?: string;
    /** 근로자/직장 */
    job_status?: string;
    /** 가구원 */
    household_type?: string;
    /** 가구원수 */
    household_size?: number;
    /** 결혼상태 */
    marriage_status?: string;
    /** 자녀 유무 */
    is_children?: boolean;
    /** 자녀 상태 */
    children_type?: string;
    /** 근로 / 직장 상세 */
    job_detail?: string;
    /** 연령대 */
    age_group?: number;
  };
  [GTM_EVENT_TYPE.SIGN_UP_STEP]: {
    /** 회원가입 단계 */
    step: number;
    /**
     * 회원가입 방법
     *
     * @example
     * '이메일', '카카오', '네이버', '애플', '구글'
     */
    method: string;
    /**
     * 고향사랑기부 회원가입, 웰로 회원가입
     *
     * @example
     * '고향사랑기부', '웰로'
     */
    signup_type: string;
  };
  [GTM_EVENT_TYPE.SIGN_UP]: {
    /** 회원가입을 한 방법 */
    method: string;
    /** 고향사랑기부 회원가입, 웰로 회원가입 */
    signup_type: string;
  };
  [GTM_EVENT_TYPE.LOGIN]: {
    /**
     * 로그인 방법
     *
     * @example
     * '이메일', '카카오', '네이버', '애플', '구글'
     */
    method: string;
  };
  [GTM_EVENT_TYPE.CLICK_GNB]: {
    /**
     * gnb 메뉴 이름
     *
     * @example
     * '로고', '웰로', '고향사랑기부', '검색', '고향사랑기부혜택'
     */
    menu_name: string;
  };
  [GTM_EVENT_TYPE.CLICK_FNB]: {
    /**
     * fnb 메뉴 이름
     *
     * @example
     * '홈', '맞춤정책', '고향사랑기부', '커뮤니티', '더보기'
     */
    menu_name: string;
  };
  [GTM_EVENT_TYPE.CLICK_SNB]: {
    /**
     * snb 메뉴 이름
     *
     * @example
     * '맞춤정책', '스크랩목록', '고향사랑기부', '상황별 정책 추천'
     */
    menu_name: string;
  };
  [GTM_EVENT_TYPE.CLICK_SETUP_MENU]: {
    /**
     * 설정 메뉴 이름
     *
     * @example
     * '알림설정', 'SNS계정연동', '공지사항', '자주묻는질문'
     */
    menu_name: string;
  };
  [GTM_EVENT_TYPE.CLICK_TAB]: {
    /**
     * tab 메뉴 이름
     *
     * @example
     * '생활·안정', '주거·자립', '교육', '고용·창업', '현금', '현물'
     */
    tab_name: string;
  };
  [GTM_EVENT_TYPE.CLICK_BTN]: {
    /**
     * 버튼 이름
     *
     * @example
     * '홈', '맞춤정책', '고향사랑기부', '커뮤니티', '더보기'
     */
    btn_name: string;
    /**
     * 컨텐츠 이름
     *
     * @example
     * 'K-패스'
     */
    content_name: string;
    /**
     * 컨텐츠 고유 ID
     *
     * @example
     * 'idx'
     */
    content_id: string;
  };
  [GTM_EVENT_TYPE.COMPLETE_POST]: {
    /** 포스트 카테고리 */
    post_category: string;
    /** 포스트 지역 */
    post_location: string;
    /** 포스트 타입 */
    post_type: string;
  };
  [GTM_EVENT_TYPE.START_POST]: {
    /** 포스트 카테고리 */
    post_category: string;
    /** 포스트 지역 */
    post_location: string;
    /** 포스트 타입 */
    post_type: string;
  };
  [GTM_EVENT_TYPE.SELECT_PROMOTION]: {
    /**
     * 프로모션의 이름
     *
     * @example
     * '수해복구'
     */
    promotion_name: string;
    /**
     * 프로모션의 고유 ID
     *
     * @example
     * '658'
     */
    promotion_id: string;
    /**
     * 클릭한 프로모션 영역명
     */
    creative_slot: PROMOTION_CREATIVE_SLOT;
  };
  [GTM_EVENT_TYPE.VIEW_PROMOTION]: {
    /**
     * 프로모션의 이름
     *
     * @example
     * '수해복구'
     */
    promotion_name: string;
    /**
     * 프로모션의 고유 ID
     *
     * @example
     * '658'
     */
    promotion_id: string;
    /**
     * 클릭한 프로모션 영역명
     */
    creative_slot?: PROMOTION_CREATIVE_SLOT;
  };
  [GTM_EVENT_TYPE.SELECT_CONTENT]: {
    /**
     * 선택한 정책의 이름
     *
     * @example
     * 'K-패스', '국민내일배움카드', '청년도약계좌'
     */
    content_name: string;
    /**
     * 선택한 이름의 카테고리
     *
     * @example
     * '정책', '커뮤니티'
     */
    content_category: CONTENT_CATEGORY;
    /**
     * 정책 고유 ID
     */
    content_id: string;
  };
  [GTM_EVENT_TYPE.VIEW_CONTENT]: {
    /**
     * 선택한 정책의 이름
     *
     * @example
     * 'K-패스', '국민내일배움카드', '청년도약계좌'
     */
    content_name: string;
    /**
     * 선택한 이름의 카테고리
     *
     * @example
     * '정책', '커뮤니티'
     */
    content_category: CONTENT_CATEGORY;
    /**
     * 정책기관
     *
     * @example
     * '국토교통부'
     */
    policy_agency?: string;
    /**
     * 정책의 고유 ID
     */
    content_id: string;
    /**
     * 정책기관의 고유 ID
     */
    policy_agency_id?: string;
  };
  [GTM_EVENT_TYPE.CLICK_BOOKMARK]: {
    /**
     * 북마크 동작 — 스크랩 추가/해제 구분
     *
     * @example
     * '추가', '해제'
     */
    action: '추가' | '해제';
    /**
     * 선택한 정책의 이름
     *
     * @example
     * 'K-패스', '국민내일배움카드', '청년도약계좌'
     */
    content_name?: string;
    /**
     * 선택한 이름의 카테고리
     *
     * @example
     * '정책', '커뮤니티'
     */
    content_category: CONTENT_CATEGORY;
    /**
     * 정책의 고유 ID
     */
    content_id: string;
    /**
     * 정책기관
     *
     * @example
     * '국토교통부'
     */
    policy_agency?: string;
    /**
     * 정책기관의 고유 ID
     */
    policy_agency_id?: string;
  };
  [GTM_EVENT_TYPE.SELECT_ITEM]: {
    /**
     * 화폐 단위
     *
     * @example
     * 'KRW'
     */
    currency: 'KRW';
    /**
     * 총 상품 가격
     */
    value?: number;
    /**
     * 선택한 상품의 지역 이름
     */
    local_name: string;
    items: {
      /**
       * 상품 타입
       *
       * @example
       * '답례품', '고향사업(지정기부)', '웰마켙상품'
       */
      item_type: '답례품' | '고향사업' | '웰마켙상품';
      /**
       * 상품 리스트 이름
       *
       * @example
       * '메인_오늘의 PICK'
       */
      item_list_name: string;
      /**
       * 상품 리스트 번호 (id)
       */
      item_list_id: string;
      /**
       * 상품가격
       */
      price?: number;
      /**
       * 상품 이름
       *
       * @example
       * '완도보이 전복담은 찰떡갈비 1팩 (6개입)'
       */
      item_name: string;
      /**
       * 상품 수량
       */
      quantity: number;
      /**
       * 상품 고유 번호
       *
       * @example
       * 'G2025000035'
       */
      item_id?: string;
      /**
       * 지정기부사업명
       */
      business_name?: string;
    }[];
  };
  [GTM_EVENT_TYPE.VIEW_ITEM]: {
    /** 화폐 단위 */
    currency: 'KRW';
    /** 총 상품 가격 */
    value: number;
    /** 선택한 상품의 지역 이름 */
    local_name: string;
    items: Array<
      GtmCommerceItem & {
        /** 지정기부사업명 */
        business_name?: string;
      }
    >;
  };
  [GTM_EVENT_TYPE.POST_COMMENT]: {
    /**
     * 댓글을 쓴 정책 (정책을 연결하거나, 정책 상세페이지에서 댓글을 작성한 경우 해당 정책 이름이 발생 )
     * 게시글의 제목이 아닌 정책의 이름으로만 발생
     * 어떤 정책에서 댓글이 많이 달렸는지를 확인하기 위해서 필요한 부분
     */
    content_name?: string;
    /**
     * 댓글을 단 콘텐츠의 제목
     */
    comment_name?: string;
    /**
     * 댓글을 쓴 카테고리
     */
    content_category: CONTENT_CATEGORY;
    /**
     * 정책의 고유한 ID
     */
    content_id: string;
  };
  [GTM_EVENT_TYPE.START_DONATION]: {
    /**
     * 선택한 상품의 지역 이름
     *
     * @example
     * '논산', '무주', '광주북구', '광주남구'
     */
    local_name: string;
  };
  [GTM_EVENT_TYPE.START_GIFT]: {
    /**
     * 선택한 상품의 지역 이름
     *
     * @example
     *
     * '논산', '무주', '광주북구', '광주남구'
     */
    local_name: string;
  };
  [GTM_EVENT_TYPE.PURCHASE]: {
    /** 주문 ID */
    transaction_id: string;
    /** 납부 방법 (welmarket) */
    payment_type?: string;
    /** 통화 */
    currency: 'KRW';
    /** 총 결제 금액 */
    value: number;
    /** 지역명 */
    local_name: string;
    /** 지정기부사업명 */
    business_name?: string;
    /** 구매 상품 목록 */
    items?: Array<{
      /** 상품 가격 */
      price: number;
      /** 상품명 */
      item_name: string;
      /** 수량 */
      quantity: number;
      /** 상품 ID */
      item_id?: string;
      /** 답례품 여부 (donation) */
      is_gift?: 'Y' | 'N';
      /** 기부 여부 (donation) */
      is_donation?: 'Y' | 'N';
      /** 상품 타입 (welmarket) */
      item_type?: '웰마켙상품';
      /** welmarket 상품 UUID */
      product_uuid?: string;
    }>;
  };
  [GTM_EVENT_TYPE.BEGIN_CHECKOUT]: {
    /** 통화 */
    currency: 'KRW';
    /** 총 결제 금액 */
    value: number;
    /** 지역명 */
    local_name: string;
    /** 지정기부사업명 */
    business_name?: string;
    /** 구매 상품 목록 */
    items: Array<{
      /** 상품 가격 */
      price: number;
      /** 상품명 */
      item_name: string;
      /** 수량 */
      quantity: number;
      /** 상품 ID */
      item_id?: string;
      /** 답례품 여부 (donation) */
      is_gift?: 'Y' | 'N';
      /** 기부 여부 (donation) */
      is_donation?: 'Y' | 'N';
      /** 상품 타입 (welmarket) */
      item_type?: '웰마켙상품';
      /** welmarket 상품 UUID */
      product_uuid?: string;
    }>;
  };
  [GTM_EVENT_TYPE.COMPLETE_REVIEW]: {
    /** 후기 상품의 지역 이름 */
    local_name: string;
    /** 후기 작성을 한 답례품 이름 */
    product_name: string;
    /** 후기 점수 */
    review_rating: number;
    /** 사진의 유무 */
    is_photo: 'Y' | 'N';
  };
  [GTM_EVENT_TYPE.INVITE_STEP]: {
    /** 초대 단계 */
    step: number;
  };
  [GTM_EVENT_TYPE.USE_POINT]: {
    product_name: string;
    product_brand: string;
    product_value: number;
    product_id: string;
  };
  [GTM_EVENT_TYPE.SELECT_POINT]: {
    product_name: string;
    product_brand: string;
    product_value: number;
    product_id: string;
  };
  [GTM_EVENT_TYPE.COMPLETE_INVITE]: {
    ticket_count: number;
  };
  [GTM_EVENT_TYPE.CLICK_POINT_BTN]: {
    point_action: string;
  };
  [GTM_EVENT_TYPE.COMPLETE_RECOMMEND]: {
    /**
     * 선택한 맞춤정책의 카테고리
     *
     * @example
     * '지역', '성별', '학력', '직장'
     */
    recommend_category: string;
  };
  [GTM_EVENT_TYPE.COMPLETE_PREFERENCE]: {
    /**
     * 선택한 관심분야의 카테고리
     *
     * @example
     * '대상특성', '생활/안정', '주거/자립', '교육', '건강'
     */
    preference_category: string;
  };
  [GTM_EVENT_TYPE.SEARCH]: {
    /** 검색을 한 장소 */
    search_type: string;
    /** 검색결과 유무 - Y/N (결과 로드 후 발화 시점에만, 검색 실행만 추적할 땐 생략) */
    search_result?: string;
    /** 검색어 */
    search_term: string;
    /** 검색 카테고리 */
    search_category: string;
  };
  [GTM_EVENT_TYPE.SHARE]: {
    /**
     * 공유 방법
     *
     * @example
     * '링크복사', '공유하기'
     */
    method: string;
    /** 공유한 콘텐츠 이름 */
    content_name: string;
    /** 공유한 콘텐츠 카테고리 */
    content_category: CONTENT_CATEGORY;
    /** 콘텐츠 고유 ID */
    content_id: string;
  };
  [GTM_EVENT_TYPE.ADD_TO_CART]: {
    /** 화폐 단위 */
    currency: 'KRW';
    /** 총 상품 가격 */
    value: number;
    /** 지역명 */
    local_name: string;
    items: GtmCommerceItem[];
  };
  [GTM_EVENT_TYPE.VIEW_CART]: {
    /** 화폐 단위 */
    currency: 'KRW';
    /** 장바구니 전체 항목 합산 금액 */
    value: number;
    items: GtmCommerceItem[];
  };
  [GTM_EVENT_TYPE.REMOVE_FROM_CART]: {
    /** 화폐 단위 */
    currency: 'KRW';
    /** 제거되는 항목의 총 가격 */
    value: number;
    items: GtmCommerceItem[];
  };
  [GTM_EVENT_TYPE.START_PURCHASE]: {
    /** 화폐 단위 */
    currency: 'KRW';
    /** 총 구매 예정 금액 */
    value: number;
    /** 지역명 */
    local_name: string;
    items: GtmCommerceItem[];
  };
};

/**
 * 문자열의 개행 문자를 공백으로 변환하는 유틸리티 함수
 */
const sanitizeValue = (value: unknown): string => {
  return String(value).replaceAll('\n', ' ');
};

/**
 * 본 속성(G-WKDK663EEQ)에도 병행 발사할 전환 이벤트.
 *
 * dataLayer push는 GTM 컨테이너를 거쳐 대행사 GA4로만 집계되므로,
 * 본 속성에서 keyEvents로 쓸 이벤트는 gtag로 한 번 더 보낸다.
 */
const GA4_CONVERSION_EVENTS: GTM_EVENT_TYPE[] = [
  GTM_EVENT_TYPE.PURCHASE,
  GTM_EVENT_TYPE.POST_COMMENT,
];

/**
 * GTM 이벤트 전송 함수
 */
export const trackGtmEvent = <T extends GTM_EVENT_TYPE>(
  eventType: T,
  params: GtmEventParams[T],
  /**
   * https://www.simoahava.com/gtm-tips/use-eventtimeout-eventcallback/
   */
  eventCallback?: () => void,
) => {
  // params의 모든 문자열 값에 sanitizeValue 적용 (1 depth만)
  const sanitizedParams = Object.entries(params).reduce(
    (acc, [key, value]) => {
      acc[key] = typeof value === 'string' ? sanitizeValue(value) : value;

      return acc;
    },
    {} as Record<string, unknown>,
  );

  if (GA4_CONVERSION_EVENTS.includes(eventType)) {
    trackGa4Event(eventType, sanitizedParams);
  }

  if (typeof window === 'undefined' || !window.dataLayer) {
    eventCallback?.();

    return;
  }

  try {
    window.dataLayer.push({
      event: eventType,
      eventCallback,
      // eventCallback을 사용할 때에는 브라우저 보안 등 환경에 따라 로그가 실행되지 않을 수 있으므로 반드시 eventTimeout을 설정해야 함
      ...(eventCallback ? { eventTimeout: 5000 } : {}),
      ...sanitizedParams,
    });
  } catch (error) {
    captureException(error);

    eventCallback?.();
  }
};

/**
 * GA4 본 속성(gtag.js로 직접 로드한 G-WKDK663EEQ)으로 이벤트를 직접 전송한다.
 * trackGtmEvent는 dataLayer 객체 push라 GTM·대행사 GA4로만 가지만,
 * gtag('event', ...)는 본 속성으로 바로 집계된다.
 */
export const trackGa4Event = (
  eventName: string,
  params: Record<string, unknown>,
) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', eventName, params);
};

/**
 * 회원가입 완료 시 Google Ads 전환(AW-323896878)을 집계한다.
 * gtag.js는 modules에서 전역 로드되며, 미로드 환경에서는 무시한다.
 */
export const trackSignUpConversion = () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', 'conversion', {
    send_to: 'AW-323896878/tP20CM_V8PUZEK6MuZoB',
    value: 1.0,
    currency: 'KRW',
  });
};

/**
 * GTM 이벤트를 위한 data 속성을 생성하는 유틸리티 함수
 */
const createGtmDataset = <T extends GTM_EVENT_TYPE>(
  eventType: T,
  params: GtmEventParams[T],
) => {
  const dataset: Record<string, string> = {
    'data-event': eventType,
  };

  // params의 모든 속성을 data-* 형태로 변환
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      dataset[`data-${key}`] = String(value);
    }
  });

  return dataset;
};

/**
 * 각 GTM 이벤트 타입별 편의 함수들
 *
 * @example
 *
 * ```tsx
 * <button {...gtmDatasets.signUpStep({
 *   step: 1,
 *   method: 'email',
 *   signup_type: 'wello',
 * })}>
 *  다음 단계
 * </button>
 * ```
 */
export const gtmDatasets = {
  userProperty: (params: GtmEventParams[GTM_EVENT_TYPE.USER_PROPERTY]) =>
    createGtmDataset(GTM_EVENT_TYPE.USER_PROPERTY, params),
  signUpStep: (params: GtmEventParams[GTM_EVENT_TYPE.SIGN_UP_STEP]) =>
    createGtmDataset(GTM_EVENT_TYPE.SIGN_UP_STEP, params),
  clickGnb: (params: GtmEventParams[GTM_EVENT_TYPE.CLICK_GNB]) =>
    createGtmDataset(GTM_EVENT_TYPE.CLICK_GNB, params),
  clickFnb: (params: GtmEventParams[GTM_EVENT_TYPE.CLICK_FNB]) =>
    createGtmDataset(GTM_EVENT_TYPE.CLICK_FNB, params),
  clickSnb: (params: GtmEventParams[GTM_EVENT_TYPE.CLICK_SNB]) =>
    createGtmDataset(GTM_EVENT_TYPE.CLICK_SNB, params),
  clickSetupMenu: (params: GtmEventParams[GTM_EVENT_TYPE.CLICK_SETUP_MENU]) =>
    createGtmDataset(GTM_EVENT_TYPE.CLICK_SETUP_MENU, params),
  clickTab: (params: GtmEventParams[GTM_EVENT_TYPE.CLICK_TAB]) =>
    createGtmDataset(GTM_EVENT_TYPE.CLICK_TAB, params),
  selectContent: (params: GtmEventParams[GTM_EVENT_TYPE.SELECT_CONTENT]) =>
    createGtmDataset(GTM_EVENT_TYPE.SELECT_CONTENT, params),
  selectItem: (params: GtmEventParams[GTM_EVENT_TYPE.SELECT_ITEM]) =>
    createGtmDataset(GTM_EVENT_TYPE.SELECT_ITEM, params),
  startDonation: (params: GtmEventParams[GTM_EVENT_TYPE.START_DONATION]) =>
    createGtmDataset(GTM_EVENT_TYPE.START_DONATION, params),
  startGift: (params: GtmEventParams[GTM_EVENT_TYPE.START_GIFT]) =>
    createGtmDataset(GTM_EVENT_TYPE.START_GIFT, params),
  purchase: (params: GtmEventParams[GTM_EVENT_TYPE.PURCHASE]) =>
    createGtmDataset(GTM_EVENT_TYPE.PURCHASE, params),
  beginCheckout: (params: GtmEventParams[GTM_EVENT_TYPE.BEGIN_CHECKOUT]) =>
    createGtmDataset(GTM_EVENT_TYPE.BEGIN_CHECKOUT, params),
  completeReview: (params: GtmEventParams[GTM_EVENT_TYPE.COMPLETE_REVIEW]) =>
    createGtmDataset(GTM_EVENT_TYPE.COMPLETE_REVIEW, params),
  clickBtn: (params: GtmEventParams[GTM_EVENT_TYPE.CLICK_BTN]) =>
    createGtmDataset(GTM_EVENT_TYPE.CLICK_BTN, params),
  inviteStep: (params: GtmEventParams[GTM_EVENT_TYPE.INVITE_STEP]) =>
    createGtmDataset(GTM_EVENT_TYPE.INVITE_STEP, params),
  selectPoint: (params: GtmEventParams[GTM_EVENT_TYPE.SELECT_POINT]) =>
    createGtmDataset(GTM_EVENT_TYPE.SELECT_POINT, params),
  clickPointBtn: (params: GtmEventParams[GTM_EVENT_TYPE.CLICK_POINT_BTN]) =>
    createGtmDataset(GTM_EVENT_TYPE.CLICK_POINT_BTN, params),
  startPost: (params: GtmEventParams[GTM_EVENT_TYPE.START_POST]) =>
    createGtmDataset(GTM_EVENT_TYPE.START_POST, params),
};

/** creativeSlot을 저장하고 이동한 페이지에서 이벤트 로깅하기 위해 사용 */
export const getCreativeSlotAndClear = () => {
  const creativeSlot = sessionStorage.getItem('fromCreativeSlot');
  sessionStorage.removeItem('fromCreativeSlot');

  return creativeSlot;
};
